export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface BlogPostingData {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  category?: string;
  keywords?: string[] | string;
}

export interface PublicProfileData {
  name: string;
  role: 'penulis' | 'penerbit' | 'organisasi';
  organization?: string;
  bio?: string;
  avatarUrl?: string;
  url: string;
  verifiedBooksCount?: number;
}

export interface BookSchemaData {
  title: string;
  uibn: string;
  authorName: string;
  publisherName: string;
  category: string;
  publicationYear: number;
  pages?: number;
  language?: string;
  synopsis?: string;
  coverUrl?: string;
  url: string;
}

/**
 * Organization schema for UIBN Platform
 */
export const createOrganizationSchema = (baseUrl: string = 'https://uibn.id') => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Universal Indonesian Book Number (UIBN)',
  alternateName: 'UIBN Indonesia',
  url: baseUrl,
  logo: `${baseUrl}/favicon.svg`,
  description: 'Sistem dan platform katalog Universal Indonesian Book Number (UIBN) resmi Indonesia.',
  sameAs: [
    'https://twitter.com/uibn_id',
    'https://www.facebook.com/uibn.id',
    'https://www.instagram.com/uibn.id',
  ],
});

/**
 * WebSite schema with search action for Google rich results
 */
export const createWebSiteSchema = (baseUrl: string = 'https://uibn.id') => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'UIBN – Universal Indonesian Book Number',
  alternateName: 'Universal Indonesian Book Number',
  url: baseUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${baseUrl}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * FAQPage Schema for interactive search snippet Accordion
 */
export const createFaqSchema = (faqs: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

/**
 * BlogPosting Schema for educational literacy & publishing articles
 */
export const createBlogPostingSchema = (post: BlogPostingData, baseUrl: string = 'https://uibn.id') => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.description,
  url: post.url,
  image: post.imageUrl ? [post.imageUrl] : [`${baseUrl}/og-default.png`],
  datePublished: post.datePublished,
  dateModified: post.dateModified || post.datePublished,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': post.url,
  },
  author: {
    '@type': 'Person',
    name: post.authorName,
  },
  publisher: {
    '@type': 'Organization',
    name: 'UIBN Indonesia',
    url: baseUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}/favicon.svg`,
    },
  },
  articleSection: post.category || 'Literasi',
  keywords: Array.isArray(post.keywords)
    ? post.keywords.join(', ')
    : typeof post.keywords === 'string'
    ? post.keywords
    : undefined,
});

/**
 * ProfilePage Schema for Author & Publisher Showcase
 */
export const createProfilePageSchema = (profile: PublicProfileData, baseUrl: string = 'https://uibn.id') => {
  const isOrgOrPublisher = profile.role === 'penerbit' || profile.role === 'organisasi';
  const entityType = isOrgOrPublisher ? 'Organization' : 'Person';

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: `Portofolio Publik ${profile.name}`,
    url: profile.url,
    mainEntity: {
      '@type': entityType,
      name: profile.name,
      description: profile.bio || `Profil resmi ${profile.role === 'organisasi' ? 'organisasi / komunitas' : profile.role} terdaftar di Platform UIBN.`,
      image: profile.avatarUrl,
      url: profile.url,
      ...(isOrgOrPublisher && profile.organization ? { legalName: profile.organization } : {}),
    },
  };
};

/**
 * CollectionPage Schema for Directory View
 */
export const createCollectionPageSchema = (
  title: string,
  description: string,
  url: string,
  items: Array<{ name: string; url: string; description?: string }>
) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: title,
  description,
  url,
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url,
      description: item.description,
    })),
  },
});

/**
 * Book Schema for certified books catalog
 */
export const createBookSchema = (book: BookSchemaData) => ({
  '@context': 'https://schema.org',
  '@type': 'Book',
  name: book.title,
  isbn: book.uibn,
  author: {
    '@type': 'Person',
    name: book.authorName,
  },
  publisher: {
    '@type': 'Organization',
    name: book.publisherName,
  },
  genre: book.category,
  datePublished: String(book.publicationYear),
  numberOfPages: book.pages,
  inLanguage: book.language || 'id',
  description: book.synopsis,
  image: book.coverUrl,
  url: book.url,
});

/**
 * BreadcrumbList Schema for Google Search breadcrumb snippets
 */
export const createBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: crumb.name,
    item: crumb.url,
  })),
});
